import type { ReactNode } from "react";

import { cx } from "./cx";

/**
 * A progress bar with its label. `fraction` is 0 to 1; `null` means the length is not known yet (the bar
 * pulses instead of filling). Screen readers get the label and the percentage.
 */
export function ProgressBar({
  label,
  fraction,
  detail,
}: {
  label: string;
  fraction: number | null;
  detail?: ReactNode;
}) {
  const percent = fraction === null ? null : Math.round(Math.min(1, Math.max(0, fraction)) * 100);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline gap-2">
        <span className="text-[13px] font-semibold text-fg">{label}</span>
        <span className="flex-1" />
        {percent !== null && <span className="text-[12.5px] text-fg-subtle tabular-nums">{percent}%</span>}
      </div>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent ?? undefined}
        className="h-2 overflow-hidden rounded-full bg-muted"
      >
        <div
          className={cx(
            "h-2 rounded-full bg-brand-500 transition-[width] duration-150",
            percent === null && "animate-pulse",
          )}
          style={{ width: percent === null ? "100%" : `${Math.max(percent, 2)}%` }}
        />
      </div>
      {detail && <span className="text-xs text-fg-subtle">{detail}</span>}
    </div>
  );
}
